import { useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Html, RoundedBox } from "@react-three/drei";
import { VisibleCanvas } from "./VisibleCanvas";

const LAYERS = [
  { key: "downpipe", label: "Downpipes", sub: "Clean stormwater", color: "#3c5a47", h: 0.10, size: [3.4, 0.1, 2.2] },
  { key: "gutter", label: "Gutters", sub: "Sized for Perth rain", color: "#62B848", h: 0.10, size: [3.5, 0.12, 2.3] },
  { key: "insulation", label: "Insulation & Sarking", sub: "Thermal + moisture barrier", color: "#cfd8d2", h: 0.14, size: [3.3, 0.14, 2.15] },
  { key: "flashing", label: "Flashings", sub: "Watertight detailing", color: "#9aa3a8", h: 0.08, size: [3.45, 0.08, 2.25] },
  { key: "sheeting", label: "Roof Sheeting", sub: "Colorbond steel, to spec", color: "#224733", h: 0.12, size: [3.6, 0.12, 2.35] },
];

function Slab({ layer, index, progressRef }) {
  const ref = useRef();
  const gap = 0.62;
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const p = progressRef.current;
    const targetY = (index - 2) * gap * (0.18 + p * 1.0);
    m.position.y = THREE.MathUtils.lerp(m.position.y, targetY, 0.08);
    const targetX = (index % 2 === 0 ? -1 : 1) * p * 0.25;
    m.position.x = THREE.MathUtils.lerp(m.position.x, targetX, 0.08);
  });
  return (
    <group ref={ref}>
      <RoundedBox args={layer.size} radius={0.03} smoothness={3} castShadow receiveShadow>
        <meshStandardMaterial color={layer.color} metalness={layer.key === "sheeting" || layer.key === "flashing" ? 0.7 : 0.15} roughness={layer.key === "insulation" ? 0.9 : 0.35} />
      </RoundedBox>
      <Html position={[layer.size[0] / 2 + 0.25, 0, 0]} center distanceFactor={6} className="pointer-events-none">
        <div className="whitespace-nowrap rounded-xl bg-white/[0.06] backdrop-blur border border-white/15 px-3 py-1.5">
          <div className="font-display font-extrabold text-white text-[13px] leading-none">{layer.label}</div>
          <div className="text-white/55 text-[10px] mt-0.5">{layer.sub}</div>
        </div>
      </Html>
    </group>
  );
}

function RoofGroup() {
  const group = useRef();
  const progressRef = useRef(0);
  useFrame((state) => {
    const el = state.gl.domElement;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    let p = 1 - (rect.top + rect.height * 0.35) / (vh * 0.85);
    p = Math.min(1, Math.max(0, p));
    progressRef.current = p;
    if (group.current) {
      const targetRotY = 0.55 + state.pointer.x * 0.25;
      group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, targetRotY, 0.05);
      group.current.rotation.x = THREE.MathUtils.lerp(group.current.rotation.x, -0.52 + state.pointer.y * 0.08, 0.05);
    }
  });
  return (
    <group ref={group} rotation={[-0.52, 0.55, 0]}>
      {LAYERS.map((l, i) => <Slab key={l.key} layer={l} index={i} progressRef={progressRef} />)}
    </group>
  );
}

export function Anatomy3D() {
  return (
    <VisibleCanvas className="w-full h-full" camera={{ position: [0, 0.5, 7], fov: 42 }} dpr={[1, 1.8]}>
      <ambientLight intensity={0.75} />
      <directionalLight position={[5, 8, 5]} intensity={1.4} />
      <directionalLight position={[-6, 2, -4]} intensity={0.4} color="#62B848" />
      <pointLight position={[0, 3, 4]} intensity={0.5} color="#bff0a8" />
      <RoofGroup />
    </VisibleCanvas>
  );
}
